// TODO(5B): replace with imports from @sunflower-games/predictions-realtime-contracts
// once the package publishes. The shapes here MUST match the contracts package
// byte-for-byte (this is the dedup contract — see realtime-architecture.md).

/**
 * Base shape every private (user-scoped) event carries. `plaeeEventId` is the
 * dedup key the {@link PrivateEventBus} keys on; both transports must produce
 * the same value for the same physical Plaee event.
 */
export interface PrivateEventBase {
  /** Plaee's event id, carried through unchanged. */
  plaeeEventId: string;
  /** Internal user id (cuid2). */
  userId: string;
  /** ISO 8601, Plaee emitter clock. */
  occurredAt: string;
}

/** Order accepted by Plaee — no fill yet. */
export interface OrderPlaced extends PrivateEventBase {
  orderId: string;
  marketId: string;
  outcomeId: string;
  side: 'buy' | 'sell';
  contractSide: 'Yes' | 'No';
  price: number;
  quantity: number;
  remaining: number;
}

/** Order filled or partially filled. */
export interface OrderFilled extends PrivateEventBase {
  orderId: string;
  marketId: string;
  outcomeId: string;
  side: 'buy' | 'sell';
  contractSide: 'Yes' | 'No';
  fillPrice: number;
  fillQuantity: number;
  remaining: number;
  status: 'filled' | 'partially-filled';
}

/** Order removed from the book. */
export interface OrderCancelled extends PrivateEventBase {
  orderId: string;
  marketId: string;
  outcomeId: string;
  reason: 'user' | 'expired' | 'market-closed' | 'system';
}

/** Net position rolled up for a user on a market/outcome. */
export interface PositionUpdated extends PrivateEventBase {
  marketId: string;
  outcomeId: string;
  netQuantity: number;
  averagePrice: number;
  realizedPnlCents: number;
  unrealizedPnlCents: number;
}

/** Single trade execution. */
export interface TradeExecuted extends PrivateEventBase {
  tradeId: string;
  marketId: string;
  outcomeId: string;
  contractSide: 'Yes' | 'No';
  price: number;
  quantity: number;
  role: 'maker' | 'taker';
}

/** Aggregated PnL update — derived from position changes. */
export interface PnlUpdated extends PrivateEventBase {
  marketId: string;
  outcomeId: string;
  realizedPnlCents: number;
  unrealizedPnlCents: number;
}

/** Wallet balance changed — always originates server-side. */
export interface BalanceChanged extends PrivateEventBase {
  currency: 'USD';
  balance: number;
  previousBalance: number;
  change: number;
  reason: 'order' | 'settlement' | 'refund' | 'withdrawal' | 'deposit' | 'manual';
}

/** Server-generated notification surfaced to the user. */
export interface NotificationCreated extends PrivateEventBase {
  notificationId: string;
  kind: string;
  title: string;
  body: string;
  meta?: Record<string, unknown>;
}

/**
 * Per-event source policy. Drives how the {@link PrivateEventBus} chooses
 * between the backend (Socket.IO) and direct (Plaee Centrifugo) paths.
 */
export type SourcePolicy =
  | { mode: 'backend-only' }
  | { mode: 'direct-only' }
  | { mode: 'prefer-direct'; fallbackMs: number }
  | { mode: 'prefer-backend'; fallbackMs: number }
  | { mode: 'both'; dedupTtlMs: number };

/** All private event names. Add here AND in {@link CanonicalPrivateEvent}. */
export type PrivateEventName =
  | 'order:placed'
  | 'order:filled'
  | 'order:partially-filled'
  | 'order:cancelled'
  | 'position:updated'
  | 'trade:executed'
  | 'pnl:updated'
  | 'balance:changed'
  | 'notification:created';

/** Mapped type from event name to its canonical payload. */
export interface CanonicalPrivateEventMap {
  'order:placed': OrderPlaced;
  'order:filled': OrderFilled;
  'order:partially-filled': OrderFilled;
  'order:cancelled': OrderCancelled;
  'position:updated': PositionUpdated;
  'trade:executed': TradeExecuted;
  'pnl:updated': PnlUpdated;
  'balance:changed': BalanceChanged;
  'notification:created': NotificationCreated;
}

/** Resolves an event name to its canonical payload shape. */
export type CanonicalPrivateEvent<E extends PrivateEventName> = CanonicalPrivateEventMap[E];

/**
 * Default policies — mirrors the "Default policies" table in
 * realtime-architecture.md. Override per-deployment via
 * {@link PrivateEventBus.setPolicy}.
 */
export const DEFAULT_PRIVATE_POLICIES: Record<PrivateEventName, SourcePolicy> = {
  'order:placed': { mode: 'prefer-direct', fallbackMs: 500 },
  'order:filled': { mode: 'prefer-direct', fallbackMs: 1000 },
  'order:partially-filled': { mode: 'prefer-direct', fallbackMs: 1000 },
  'order:cancelled': { mode: 'both', dedupTtlMs: 5000 },
  'position:updated': { mode: 'backend-only' },
  'trade:executed': { mode: 'prefer-direct', fallbackMs: 1000 },
  'pnl:updated': { mode: 'prefer-backend', fallbackMs: 2000 },
  'balance:changed': { mode: 'backend-only' },
  'notification:created': { mode: 'backend-only' },
};

/**
 * Deterministic id used as the dedup key by the {@link PrivateEventBus}.
 *
 * Both transports — backend webhook path (api → Socket.IO) and FE direct
 * Centrifugo path — MUST produce the same id for the same physical Plaee
 * event, or `mode: 'both'` and the prefer-X fallback policies silently
 * double-emit.
 *
 * **This implementation MUST match the api-side counterpart in
 * `@sunflower-games/predictions-realtime-contracts` byte-for-byte.** When 5B
 * publishes for real, replace this local copy with an import.
 *
 * `naturalKey` rules per event type (kept in lockstep with the api-side
 * `PlaeePrivateEventHandler` and this file's adapter):
 *  - order_placed / order_filled / order_partial_fill / order_cancelled:
 *      `${orderId}|${ordStatusUpper}|${cumQty}|${leavesQty}`
 *  - trade_executed:
 *      `trade|${tradeReportId}`
 *  - position_updated / pnl_updated: not computed here — those events are
 *    `backend-only` policy, so the adapter returns null and the bus never
 *    sees a `direct` source for them. If that policy changes, mirror the
 *    api-side naturalKey rules.
 */
export interface PlaeeEventIdParts {
  type: string;
  userId: string;
  naturalKey: string;
}

export function computePlaeeEventId(parts: PlaeeEventIdParts): string {
  return `${parts.type}|${parts.userId}|${parts.naturalKey}`;
}
